import React, {
    useCallback, useContext, useEffect, useMemo, useState
} from 'react';
import { useRouter } from 'next/router';
import { IRS_RAIL_BUTTON_LIST } from 'lib/constants';
import { socket, socketConnect, socketEmit } from 'services/socket';
import useIrsContextStore from 'store/irsContextStore';
import SidebarSection from '@/molecules/RailButton';
import { DataContext } from '@/components/context/DataContext';

const RailSideBar = () => {
    const router = useRouter();
    const context = useIrsContextStore((state) => state);
    const { setActiveRatesCcy } = useContext(DataContext);
    const {
        id, command: commandFromQuery, advanced = false, legs, depId, l1 = null, indepId = null, l2 = null, str = null, depF = null, indepF = null, immFwd, immLabel, depImm, indepImm, meetingDate, plotCommand, plotType
    } = router.query;
    const shouldShowRail = ['/plot/irs/[id]', '/regress', '/anchor', '/chronos/[id]', '/arc/irs/[id]', '/cta', '/edge/irs/[id]'].includes(router.pathname);
    if (!shouldShowRail) return <></>;
    const sections = useMemo(() => IRS_RAIL_BUTTON_LIST ?? [], []);
    const [referenceIrs, setReferenceIrs] = useState(null);
    const [resolvedSecurityId, setResolvedSecurityId] = useState(null);
    const [irs, setIrs] = useState(null);
    const [defaultId, setDefaultId] = useState(null);

    const activeCommand = useMemo(() => {
        if (router.pathname === '/cta') return 'CTASCAN';
        if (router.pathname === '/regress') return 'REGRESS';
        if (router.pathname === '/anchor') return 'ANCHOR';
        if (router.pathname?.startsWith('/arc/irs/')) return 'ARC';
        if (router.pathname?.startsWith('/edge/irs/')) return 'EDGE';
        if (router.pathname?.startsWith('/chronos')) return 'TRACE';
        if (router.pathname?.startsWith('/plot/irs/')) {
            if (advanced) return 'PLOTA';
            if (commandFromQuery?.toLowerCase() === 'basis') return 'BASIS';
            return 'PLOT';
        }
        return commandFromQuery || '';
    }, [router.pathname, commandFromQuery, advanced]);

    const activeCommandUpper = useMemo(
        () => String(activeCommand || '').toUpperCase(),
        [activeCommand]
    );

    const plotId = id ?? depId ?? indepId ?? defaultId;
    const securityId = resolvedSecurityId ?? plotId;
    const plotLegs = legs ?? l1 ?? l2 ?? str ?? '1y';
    const plotStr = str ?? legs ?? l1 ?? l2;
    const isFwd = commandFromQuery === 'fwcm' || plotCommand === 'fwcm' || depF === '1' || indepF === '1';
    const isImm = Boolean(immFwd) || depImm === '1' || indepImm === '1';

    const onIrsEvent = (data) => {
        if (data?.error) {
            return;
        }
        setIrs(data);
    }

    const onGetIrsSecurity = (data) => {
        if (data?.error) return;
        if (data?.id) setResolvedSecurityId(String(data.id));
    };
    const onGetIrsSecurityByIrsId = (data) => {
        if (data?.error) return;
        if (data?.id) setResolvedSecurityId(String(data.id));
    };

    const onAllIrsEvent = (data) => {
        if (!Array.isArray(data)) return;
        const extractId = (item) => item?.id;
        const inrNdois = data.find((item) => item?.currency === 'INR');
        const resolvedId = extractId(inrNdois);
        if (resolvedId) setDefaultId(resolvedId);
    };

    useEffect(() => {
        socketConnect();
        socket.on('/irs/reference', setReferenceIrs);
        socket.on('/get/irs-securities/:id', onGetIrsSecurity);
        socket.on('/get/irs-securities/:irsId', onGetIrsSecurityByIrsId);
        socket.on('/plots/irs/:id', onIrsEvent);
        socket.on('/get/irs', onAllIrsEvent);
        socketEmit('/irs/reference');
        socketEmit('/get/irs');

        return () => {
            socket.off('/irs/reference', setReferenceIrs);
            socket.off('/get/irs-securities/:id', onGetIrsSecurity);
            socket.off('/get/irs-securities/:irsId', onGetIrsSecurityByIrsId);
            socket.off('/plots/irs/:id', onIrsEvent);
            socket.off('/get/irs', onAllIrsEvent);
        };
    }, []);

    useEffect(() => {
        if (!router.isReady) return;

        if (router.pathname === '/irs/[id]' && id) {
            socketEmit('/get/irs-securities/:id', { id });
            socketEmit('/get/irs-securities/:id', { id });
            return;
        }

        if (router.pathname === '/plot/irs/[id]' && id) {
            const raw = (legs ?? l1 ?? str ?? '').toString();
            const term = raw
                .split(',')
                .map((t) => t.trim().toUpperCase().replace(/^(\d+(?:\.\d+)?)([A-Za-z])$/, '$1 $2'))
                .join(',');
            if (!term) return;
            socketEmit('/get/irs-securities/:irsId', { irsId: id, term });
            socketEmit('/plots/irs/:id', { id });
        }
    }, [router.isReady, router.pathname, id, legs, l1, str]);

    const normalizeLegsParam = useCallback((raw) => {
        const normalized = String(raw ?? '').trim().toLowerCase();
        if (!normalized) return '1y';
        const isValid = (val) => /^\d+(?:\.\d+)?[dwmy]$/i.test(val);
        if (normalized.includes(',')) {
            const parts = normalized
                .split(',')
                .map(p => p.trim())
                .filter(Boolean)
                .map(p => (isValid(p) ? p : '1y'));

            return parts.length ? parts.join(',') : '1y';
        }

        // Single value
        return isValid(normalized) ? normalized : '1y';
    }, []);

    const buildNextPlotQuery = useCallback(({ advanced: nextAdvanced = false } = {}) => {
        const next = { ...router.query };
        ['depId', 'indepId', 'l1', 'l2', 'depF', 'indepF', 'depImm', 'indepImm', 'id'].forEach((k) => {
            delete next[k];
        });

        const legsParam = normalizeLegsParam(next.legs ?? plotLegs ?? plotStr);
        if (legsParam) next.legs = legsParam;

        if (isFwd || next.command === 'fwcm') next.command = 'fwcm';
        else delete next.command;

        if (immFwd) next.immFwd = immFwd;
        if (immLabel) next.immLabel = immLabel;
        if (meetingDate) next.meetingDate = meetingDate;

        if (nextAdvanced) next.advanced = 'true';
        else delete next.advanced;

        return next;
    }, [router.query, normalizeLegsParam, plotLegs, plotStr, isFwd, immFwd, immLabel, meetingDate]);

    const handleRailButtonClick = useCallback((nextCommandRaw) => {
        const nextCommand = String(nextCommandRaw || '').toUpperCase();
        if (!nextCommand) return;
        if (activeCommandUpper === nextCommand) return;

        switch (nextCommand) {
            case 'TRACE': {
                if (!plotId) return;
                const legsParam = normalizeLegsParam(plotLegs ?? plotStr);
                router.push({
                    pathname: `/chronos/${plotId}`,
                    query: {
                        ...(str && { str }),
                        ...(meetingDate && { str: meetingDate }),
                        legs: legsParam,
                        ...(isFwd ? { command: 'fwcm' } : {}),
                        ...(immFwd ? { immFwd } : {}),
                        ...(immLabel ? { immLabel } : {}),
                        ...(plotType ? { plotType } : {}),
                        ...(meetingDate ? { meetingDate } : {})
                    }
                });
                return;
            }
            case 'REGRESS': {
                const dep = depId ?? id ?? plotId;
                const indep = indepId ?? referenceIrs?.data?.id ?? dep;
                const s1Leg = String(context.structure1 ?? '').trim();
                const s2 = (l2 ?? l1 ?? legs ?? str ?? context.structure2) ?? '1y';
                if (!dep || !s2) return;
                router.push(
                    `/regress?depId=${dep}&indepId=${dep}&l2=${s2}${isFwd ? '&depF=1&indepF=1' : ''}${isImm ? `&depImm=1&indepImm=1&immFwd=${immFwd}` : ''}${meetingDate ? `&meetingDate=${meetingDate}` : ''}&s2=1${s1Leg ? `&s1Leg=${s1Leg}` : ''}${immLabel ? `&immLabel=${immLabel}` : ''}${plotType ? `&plotType=${plotType}` : ''}`
                );
                return;
            }
            case 'ANCHOR': {
                const dep = depId ?? id ?? plotId;
                const indep = indepId ?? '';
                const s1 = (l1 ?? legs ?? str ?? l2) ?? '1y';
                if (!dep || !s1) return;
                router.push(
                    `/anchor?l1=${s1}&depId=${dep}&indepId=${dep}${isFwd ? '&depF=1' : ''}${isImm ? `&depImm=1&immFwd=${immFwd}` : ''}${meetingDate ? `&meetingDate=${meetingDate}` : ''}${immLabel ? `&immLabel=${immLabel}` : ''}${plotType ? `&plotType=${plotType}` : ''}`
                );
                return;
            }
            case 'ARC': {
                if (!plotId) return;
                const legsParam = normalizeLegsParam(plotLegs ?? plotStr);
                router.push({
                    pathname: `/arc/irs/${plotId}`,
                    query: {
                        ...(meetingDate && { str: meetingDate }),
                        legs: legsParam,
                        ...(isFwd ? { plotCommand: 'fwcm' } : {}),
                        ...(immFwd ? { immFwd } : {}),
                        ...(immLabel ? { immLabel } : {}),
                        ...(meetingDate ? { meetingDate } : {}),
                        ...(immLabel ? { immLabel } : {}),
                        ...(plotType ? { plotType } : {}),
                    }
                });
                return;
            }
            case 'PLOT': {
                if (!plotId) return;
                router.push(
                    { pathname: `/plot/irs/${plotId}`, query: buildNextPlotQuery({ advanced: false }) },
                    undefined,
                    { shallow: false }
                );
                return;
            }
            case 'PLOTA': {
                if (!plotId) return;
                router.push(
                    { pathname: `/plot/irs/${plotId}`, query: buildNextPlotQuery({ advanced: true }) },
                    undefined,
                    { shallow: false }
                );
                return;
            }
            case 'BASIS': {
                const dep = depId ?? plotId;
                const refId = referenceIrs?.data?.id ?? referenceIrs?.id ?? referenceIrs?.value?.id;
                const indep = indepId ?? refId ?? plotId;
                const basisLegs = normalizeLegsParam(plotLegs ?? plotStr);
                if (!dep || !indep || !basisLegs) return;

                router.push({
                    pathname: `/plot/irs/${plotId}`,
                    query: {
                        command: 'BASIS',
                        depId: dep,
                        indepId: indep,
                        l1: basisLegs,
                        l2: basisLegs,
                        ...(isFwd ? { depF: '1', indepF: '1' } : {}),
                        ...(isImm ? { depImm: '1', indepImm: '1', immFwd } : {}),
                        ...(meetingDate ? { meetingDate } : {}),
                        ...(immLabel ? { immLabel } : {}),
                        ...(plotType ? { plotType } : {}),
                    }
                }, undefined, { shallow: false });
                return;
            }
            case 'EDGE': {
                if (!plotId) return;
                const legsParam = normalizeLegsParam(plotLegs ?? plotStr);
                router.push({
                    pathname: `/edge/irs/${plotId}`,
                    query: {
                        ...(meetingDate && { str: meetingDate }),
                        legs: legsParam,
                        ...(isFwd ? { plotCommand: 'fwcm' } : {}),
                        ...(immFwd ? { immFwd } : {}),
                        ...(immLabel ? { immLabel } : {}),
                        ...(meetingDate ? { meetingDate } : {}),
                        ...(immLabel ? { immLabel } : {}),
                        ...(plotType ? { plotType } : {}),
                    }
                });
                return;
            }
            case 'CTASCAN': {
                if (!plotId || !irs) return;
                const legsParam = normalizeLegsParam(plotLegs ?? plotStr);
                setActiveRatesCcy(irs?.currency);
                router.push({
                    pathname: `/cta`,
                    query: {
                        ...(meetingDate && { str: meetingDate }),
                        legs: legsParam,
                        id: plotId,
                        ...(isFwd ? { plotCommand: 'fwcm' } : {}),
                        ...(immFwd ? { immFwd } : {}),
                        ...(immLabel ? { immLabel } : {}),
                        ...(meetingDate ? { meetingDate } : {}),
                        ...(immLabel ? { immLabel } : {}),
                        ...(plotType ? { plotType } : {}),
                    }
                });
                return;
            }
            default:
        }
    }, [
        activeCommandUpper,
        buildNextPlotQuery,
        commandFromQuery,
        depF,
        depId,
        id,
        indepF,
        indepId,
        isFwd,
        isImm,
        l1,
        l2,
        legs,
        normalizeLegsParam,
        plotId,
        plotLegs,
        plotStr,
        router,
        str,
        referenceIrs,
        securityId,
        irs,
        defaultId
    ]);

    return (
        <div className="flex flex-col px-3 gap-2.5 border-r border-gray-600 h-screen">
            <div className='flex justify-center items-center pt-7 pb-2'>
                <img src='/images/icons/green-bird.svg' alt="Green Bird" width={45} />
            </div>
            {sections.map((item) => (
                <SidebarSection
                    key={item.command || item.name}
                    name={item.name}
                    title={item.section}
                    command={item.command}
                    items={item.items}
                    hoverText={item.hoverText}
                    active={activeCommandUpper}
                    onSelect={handleRailButtonClick}
                />
            ))}
        </div>
    );
};

export default RailSideBar;
